import { booleanAttribute, Component, computed, DoCheck, effect, EventEmitter, forwardRef, inject, Input, input, OnDestroy, OnInit, Output, signal, ViewChild, Directive, ElementRef } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { cloneDeep, isEmpty } from 'lodash';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { OverlayPanel } from 'primeng/overlaypanel';
import { lastValueFrom, Subscription } from 'rxjs';
import { HisControlValueAccessor } from '../../core/forms/his-control-value-accessor';
import { GenericValue } from '../../core/model/generic-value';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HelptextProvider, HisPlamComponent, LabelProvider, RequiredProvider } from '../../structure/component/plam/his-plam.component';
import { HisUploadConfiguration } from '../model/his-upload-configuration';
import { FilesChangedEvent, FilesMutatedEvent, HisUploadFile } from '../model/his-upload-file';
import { ResourceFeedback } from '../model/resource-feedback';
import { UploadFieldConfigResponse } from '../model/upload-field-config-response';
import { HisFileUploadService } from '../service/his-file-upload.service';
import { HisFileUtil } from '../util/his-file-util';
import { HisUploadFileUtil } from '../util/his-upload-file-util';
import { HisBackendService } from './../../core/service/backend/his-backend.service';
import { HisScriptLoaderService } from './../../core/service/base/his-script-loader.service';
import { HisNavigationService } from '../../core/service/base/his-navigation.service';
import { HisDocumentMetadataCategoryService } from '../service/his-document-metadata-category.service';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';

declare const tus: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Upload: new ( file: File, options: {
        endpoint: string;
        retryDelays?: number[];
        metadata?: { [key: string]: string };
        onError?: ( error: Error ) => void;
        onProgress?: ( bytesUploaded: number, bytesTotal: number ) => void;
        onSuccess?: () => void;
    } ) => {
        url?: string;
        start(): void;
        abort(): void;
        findPreviousUploads(): Promise<any[]>;
    };
};

/**
 * Abstract component with the implementation of the two way value binding.
 */
@Directive( {
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef( () => HisAbstractFileUploadComponent ),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef( () => HisAbstractFileUploadComponent ),
            multi: true,
        },
    ],
    standalone: false
} )
export abstract class HisAbstractFileUploadComponent extends HisControlValueAccessor implements OnInit, DoCheck, LabelProvider, HelptextProvider, RequiredProvider, OnDestroy, Validator {
    private uploadService = inject( HisFileUploadService );
    protected hisUiService = inject( HisUiService );
    private controlDirective = inject( NgControl, { self: true } );
    private parentPlam = inject<HisPlamComponent>( HisPlamComponent, { optional: true } );
    private scriptLoaderService = inject( HisScriptLoaderService );
    private hisBackendService = inject( HisBackendService );
    private readonly host = inject<ElementRef<HTMLElement>>( ElementRef );


    // Muss Required sein
    @Input( { required: true } ) configKey!: string; //Beispiel "cm.alu.usermanagement.importMembers.importMembers.selectUploadFile"

    /** enum JavaScriptConfigurationContext */
    @Input() javascriptContext: string;

    // Die Map wird nicht serialisiert, das muss wohl vor der Übergabe an den BackendService strigified werden
    /** contextObjects werden in der Konfiguration definiert */
    @Input() contextObjects: Map<string, any>;
    @Input() styleClass: string;
    @Input() placeholder: string;
    @Input() disableUploadOnly = false;

    /** Erlaubt es Nutzern der Komponente, die Anzeige des Kommentarfeldes programmatisch zu überschreiben. */
    readonly disableComment = input( false, { transform: booleanAttribute } );

    /** Erlaubt es Nutzern der Komponente, die Anzeige des Hilfetext-Icons (helptextIcon) programmatisch zu unterbinden. */
    readonly disableHelptext = input( false, { transform: booleanAttribute } );

    /** Erlaubt es Nutzern der Komponente, die Anzeige des Hilfetextes oberhalb (helptextAbove) programmatisch zu unterbinden. */
    readonly disableHelptextAbove = input( false, { transform: booleanAttribute } );

    /** Erlaubt es Nutzern der Komponente, die Anzeige des Hilfetextes unterhalb (helptextBelow) programmatisch zu unterbinden. */
    readonly disableHelptextBelow = input( false, { transform: booleanAttribute } );

    /** Erlaubt es Nutzern der Komponente, die Anzeige und Berechnung des Datei-Feedbacks programmatisch zu unterbinden. */
    readonly disableFeedback = input( false, { transform: booleanAttribute } );

    /** Überschreibt die Konfigurationseinstellung {@link HisUploadConfiguration.multiple}. Ist der Wert {@code undefined}, gilt die Einstellung aus der Konfiguration. */
    readonly multiple = input<boolean | undefined, unknown>( undefined, { transform: booleanAttribute } );

    /** Überschreibt die Konfigurationseinstellung {@link HisUploadConfiguration.showCategoryIfUnique}. Ist der Wert {@code undefined}, gilt die Einstellung aus der Konfiguration. */
    readonly showCategoryIfUnique = input<boolean | undefined, unknown>( undefined, { transform: booleanAttribute } );

    /** Überschreibt die Konfigurationseinstellung {@link HisUploadConfiguration.documentMetadataCategoryIds}. Ist der Wert {@code undefined}, gilt die Einstellung aus der Konfiguration. */
    readonly categoryIds = input<number[] | undefined>( undefined );

    @Output() filesChanged = new EventEmitter<FilesChangedEvent>();
    @Output() fileAdded = new EventEmitter<FilesMutatedEvent>();
    @Output() fileRemoved = new EventEmitter<FilesMutatedEvent>();

    // Zugriff auf p-fileUpload, wird in der Methode uploadImage verwendet
    @ViewChild( 'pFileUpload' ) pFileUpload: FileUpload;

    @ViewChild( 'feedbackPanel' ) feedbackPanel: OverlayPanel;
    @ViewChild( 'uploadButtonRef' ) uploadButton: ElementRef<HTMLButtonElement>;

    private requiredOverride: boolean | undefined;

    /** Überschreibt die Konfigurationseinstellung {@link HisUploadConfiguration.required}. Ist der Wert {@code undefined}, gilt die Einstellung aus der Konfiguration. */
    @Input( { transform: booleanAttribute } )
    set required( value: boolean ) {
        this.requiredOverride = value;
    }
    get required(): boolean {
        return this.requiredOverride ?? this.configuration?.required ?? false;
    }
    ddLabeltext: string;
    ddHelptext: string;

    /** two way value binding */
    protected model: HisUploadFile[] = [];
    protected oldModel: HisUploadFile[] = [];
    protected configuration: HisUploadConfiguration;

    protected feedbackPanelContent: string;

    /** Backing signal of the {@link uploading} accessor. */
    private readonly uploadingState = signal( false );

    /**
     * Counter bumped by {@link uploadQueueChanged} whenever {@link uploadQueue} is mutated.
     * The queue itself is a plain array that is modified in place, so it cannot be tracked
     * by {@link uploadActive} directly.
     */
    private readonly uploadQueueVersion = signal( 0 );

    protected supportLargeFiles = false;
    protected uploadQueue: { file: File; upload: any; progress: number; paused: boolean; }[] = [];
    protected scriptLoaded = false;

    /**
     * `true` while a file is actively being uploaded and the transfer is not (fully) paused.
     * Consumers (e.g. the surrounding selection overlay) use this to lock their apply/cancel
     * actions during an active upload, mirroring the JSF behaviour. When every large-file
     * transfer is paused the flag is `false` again, so the user may apply or cancel the
     * paused selection.
     */
    readonly uploadActive = computed<boolean>( () => {
        this.uploadQueueVersion();

        if ( !this.uploadingState() ) {
            return false;
        }

        return this.uploadQueue.length === 0 || this.uploadQueue.some( ue => !ue.paused );
    } );

    /** `true` while an upload request is in flight. */
    protected get uploading(): boolean {
        return this.uploadingState();
    }

    protected set uploading( value: boolean ) {
        this.uploadingState.set( value );
    }

    protected readonly allAssignableConfigurationDmcValues = signal<GenericValue[]>( [] );
    protected readonly selectedCategory = signal<GenericValue | null>( null );
    protected readonly filteredCategories = signal<GenericValue[]>( [] );
    protected readonly showCategorySelect = computed( () =>
        this.allAssignableConfigurationDmcValues().length > 1
    );
    protected readonly showDescription = computed( () =>
        !!this.configuration?.showDescription && !this.disableComment()
    );
    protected readonly helptextAbove = computed( () =>
        this.disableHelptextAbove() ? null : this.configuration?.helptextAbove
    );
    protected readonly helptextBelow = computed( () =>
        this.disableHelptextBelow() ? null : this.configuration?.helptextBelow
    );
    protected readonly isMultiple = computed( () =>
        this.multiple() ?? this.configuration?.multiple
    );
    protected readonly isShowCategoryIfUnique = computed( () =>
        this.showCategoryIfUnique() ?? this.configuration?.showCategoryIfUnique
    );
    private readonly effectiveCategoryIds = computed( () =>
        this.categoryIds() ?? this.configuration?.documentMetadataCategoryIds
    );
    private documentMetadataCategoryService = inject( HisDocumentMetadataCategoryService );

    private files: File[] = [];
    private isMaxUploadsReached = false;
    private control: AbstractControl;
    private disabledIntern: boolean;
    private feedback: Map<string, ResourceFeedback[]> = new Map<string, ResourceFeedback[]>();
    protected ongoingBatchFeedback: Subscription;
    private largeFileSupport = false;

    private defaultUploadOptions: any = {
        chunkSize: 5 * 1024 * 1024, // 5 MB
        retryDelays: [0, 1000, 3000, 5000],
        parallelUploads: 1,
        storeFingerprintForResuming: true,
        removeFingerprintOnSuccess: true
    };

    constructor() {
        super();
        const controlDirective = this.controlDirective;

        controlDirective.valueAccessor = this;
        this.defaultUploadOptions.endpoint = `${inject( HisNavigationService ).getQisserverUrl()}/files/`;

        effect( () => {
            const files = this.model;
            const dmcs = this.allAssignableConfigurationDmcValues();
            const config = this.configuration;
            const current = this.selectedCategory();

            if ( !config || !files?.length || !this.isShowCategoryIfUnique() || dmcs?.length !== 1 ) {
                return;
            }

            const category = dmcs[0];

            if ( current?.id === category.id ) {
                this.setDocumentMetadataCategoryId( category );
                return;
            }

            this.selectedCategory.set( category );
            this.setDocumentMetadataCategoryId( category );
        } );
    }

    ngOnInit() {
        this.control = this.controlDirective.control;

        console.log( 'ngOnInit() #this.configKey', this.configKey );

        this.uploadService.getUploadFieldConfig( this.configKey, this.javascriptContext, this.contextObjects )
            .subscribe( ( response: UploadFieldConfigResponse ) => {
                if ( !response ) {
                    return;
                }
                console.log( 'ngOnInit() #response', response );
                this.configuration = new HisUploadConfiguration( response );
                this.ddLabeltext = this.configuration.label;
                this.ddHelptext = this.disableHelptext() ? '' : this.configuration.helptextIcon;
                this.supportLargeFiles = this.configuration.uploadForLargeFiles;
                this.largeFileSupport = this.configuration.uploadForLargeFiles;
                this.triggerValidation();

                if ( this.supportLargeFiles ) {
                    this.scriptLoaderService.loadJSFScript( 'tus.js', 'js/extras/tus-js-client/dist' )
                        .then( () => this.scriptLoaded = true );
                }

                this.loadDocumentMetadataCategories();
            } );
    }

    ngDoCheck() {
        if ( !this.model || !this.oldModel ) {
            return;
        }

        if ( this.model.length === this.oldModel.length ) {
            return;
        }

        console.log( 'ngDoCheck() #modelChanged #this.model', this.model );
        this.oldModel = cloneDeep( this.model );

        // model has changed!
        // update isMaxUploadsReached; but only if the configuration is already loaded
        if ( this.configuration ) {
            this.isMaxUploadsReached = this.uploadService.getIsMaxUploadsReached(
                this.isMultiple(),
                this.configuration?.fileLimit,
                this.model );
        }
    }

    triggerValidation() {
        this.control.updateValueAndValidity();
    }

    private initializeFeedback() {
        if ( this.disableFeedback() ) {
            return;
        }

        if ( isEmpty( this.model ) ) {
            return;
        }

        this.ongoingBatchFeedback?.unsubscribe();
        this.ongoingBatchFeedback = this.uploadService.batchProvideFeedback( this.configKey, this.model, this.javascriptContext, this.contextObjects ).subscribe( feedbackMap => {
            this.ongoingBatchFeedback = null;
            feedbackMap.forEach( ( feedback, uuid ) => {
                this.feedback.set( uuid, feedback );
            } );
        } );
    }

    private getFeedbackForFile( file: HisUploadFile ) {
        if ( this.disableFeedback() ) {
            return;
        }

        if ( this.feedback.has( file.uuid ) ) {
            return;
        }

        const uploadFeedback = this.uploadService.provideFeedback( this.configKey, file, this.javascriptContext, this.contextObjects ).subscribe( aFeedback => {
            this.feedback.set( file.uuid, aFeedback );
            this.uploadService.abortFeedbackRequest( file.uuid );
        } );
        this.uploadService.registerFeedbackRequest( file.uuid, uploadFeedback );
    }

    validate( control: AbstractControl ): ValidationErrors | null {
        const errors: ValidationErrors = {
            ...this.validateRequired(),
            ...this.validateMetadatacategory( control ),
        };

        return Object.keys( errors ).length > 0 ? errors : null;
    }

    private validateRequired(): ValidationErrors | null {
        if ( !this.required ) {
            return null;
        }

        if ( this.disabledIntern ) {
            return null;
        }

        if ( this.model?.length > 0 ) {
            return null;
        }

        let error = {
            filesMissing: {
                message: this.hisUiService.translate( 'validation.required' )
            }
        };
        return error;
    }

    protected showValidationMessage(): boolean {
        if ( this.parentPlam ) {
            return false;
        }

        return this.control?.hasError( 'filesMissing' );
    }

    onValidationChange: any = () => { };

    protected get deleteDisabled(): boolean {
        return this.disabledIntern || this.configuration?.readonly;
    }

    protected get downloadDisabled(): boolean {
        // download should be possible, even if the field is disabled see https://hiszilla.his.de/hiszilla/show_bug.cgi?id=309356#c8 for details
        return false;
    }

    protected get commentDisabled(): boolean {
        return this.disabledIntern || this.configuration?.readonly;
    }

    protected get categoryDisabled(): boolean {
        return this.disabledIntern || this.configuration?.readonly;
    }

    // *** Implementation of ControlValueAccessor start

    override setDisabledState?( isDisabled: boolean ): void {
        this.disabledIntern = isDisabled;
    }

    registerOnValidatorChange?( fn: () => void ): void {
        this.onValidationChange = fn;
    }

    override writeValue( value: any ): void {
        this.model = value;
        this.uploadService.cleanupFeedback( this.model );
        this.initializeFeedback();
    }

    // *** Implementation of ControlValueAccessor end

    protected validateMetadatacategory( control: AbstractControl ): ValidationErrors | null {
        if ( !this.model ) {
            return null;
        }

        if ( this.effectiveCategoryIds()?.length < 2 ) {
            return null;
        }

        for ( let file of this.model ) {
            if ( !file.documentMetadataCategoryId ) {
                let error = {
                    categoryMissing: {
                        message: this.hisUiService.translate( 'cs.sys.hisng.upload.documentMetadataCategory.requiredMessage' )
                    }
                };
                return error;
            }
        }

        return null;
    }

    protected isShowUpload(): boolean {
        if ( this.disabledIntern || this.disableUploadOnly ) {
            return false;
        }

        if ( this.isMaxUploadsReached ) {
            return false;
        }

        if ( this.model?.length === this.configuration?.fileLimit ) {
            return false;
        }

        if ( this.isMultiple() === false && this.model?.length > 0 ) {
            return false;
        }

        if ( this.configuration?.readonly === true ) {
            return false;
        }

        return true;
    }

    private addNewFilesFromStandardUpload( selectedFiles: File[] | FileList ) {
        // wird ausgelöst, wenn ein File ausgewählt wird
        if ( !this.model ) {
            this.model = [];
        }
        let limitedUploadedFiles = this.uploadService.limitNumberOfFilesToTheConfuguredFileLimit( selectedFiles, this.configuration, this.model );

        lastValueFrom( this.uploadService.getUploadFiles( limitedUploadedFiles ) )
            .then( ( selectedUploadFiles: HisUploadFile[] ) => this.addNewFiles( selectedUploadFiles ) );
    }

    /**
     * Wird beim Hinzufügen (onSelect) eines oderer mehrer Files ausgelöst
     *
     */
    private addNewFiles( selectedUploadFiles: HisUploadFile[] ) {
        if ( !this.model ) {
            this.model = [];
        }
        const oldFiles = cloneDeep( this.model );

        const newUploadFiles = HisUploadFileUtil.getNotContainedFiles( this.model, selectedUploadFiles );

        this.model.push( ...newUploadFiles );
        this.filesChanged.emit( new FilesChangedEvent( oldFiles, cloneDeep( this.model ) ) );
        newUploadFiles?.forEach( file => {
            this.fileAdded.emit( new FilesMutatedEvent( newUploadFiles[0], cloneDeep( this.model ) ) );
            this.getFeedbackForFile( file );
        } );
        this.onModelChange( this.model );

        // Setzt das p-fileupload zurück. Das ist notwendig für die Möglichkeit,
        // falls multiple=true mehrere Auswahlen nacheinander durchzuführen
        if ( this.pFileUpload ) {
            this.pFileUpload.clear();
        }

        this.triggerValidation();
        this.uploading = false;

        if ( newUploadFiles?.length ) {
            this.focusFileName( newUploadFiles[0] );
        }
    }

    protected onBeforeUpload(): void {
        this.uploading = true;
        this.triggerValidation();
    }

    protected remove( uploadFile: HisUploadFile ) {
        console.log( 'changes #remove #file', uploadFile );

        const oldFiles = cloneDeep( this.model );

        this.model = HisUploadFileUtil.removeHisUploadFile( this.model, uploadFile );
        console.log( 'changes #remove #afteRemove #this.model', this.model );
        this.files = HisFileUtil.removeHisUploadFile( this.files, uploadFile );
        console.log( 'changes #remove #afteRemove #this.files', this.files );

        this.isMaxUploadsReached = false;
        this.filesChanged.emit( new FilesChangedEvent( oldFiles, cloneDeep( this.model ) ) );
        this.fileRemoved.emit( new FilesMutatedEvent( uploadFile, cloneDeep( this.model ) ) );
        this.removeFeedback( uploadFile.uuid );
        this.onModelChange( this.model );
        this.triggerValidation();
        this.restoreFocus();
    }

    private restoreFocus() {
        setTimeout( () => this.uploadButton?.nativeElement.focus() );
    }

    /** Der Name der hinzugefuegten Datei ist erst nach der naechsten Change Detection im DOM. */
    private focusFileName( file: HisUploadFile ) {
        setTimeout( () => this.host.nativeElement
            .querySelector<HTMLElement>( `[data-file-uuid="${file.uuid}"]` )
            ?.focus() );
    }

    private removeFeedback( uploadFileUuid: string ) {
        this.uploadService.abortFeedbackRequest( uploadFileUuid );
        this.feedback.delete( uploadFileUuid );
    }

    protected setDocumentMetadataCategoryId( category: GenericValue | null ) {
        console.log( 'setDocumentMetadataCategoryId #category', category );

        const oldFiles = cloneDeep( this.model );
        for ( const uploadFile of this.model ) {
            uploadFile.documentMetadataCategoryId = category?.id;
        }
        this.triggerValidation();

        this.filesChanged.emit( new FilesChangedEvent( oldFiles, cloneDeep( this.model ) ) );
    }

    protected onCategoryChange( event: Event ): void {
        const selectedValue = ( event.target as HTMLSelectElement ).value;

        if ( !selectedValue ) {
            this.setDocumentMetadataCategoryId( null );
        }
    }

    protected onCommentChanged( file: HisUploadFile, comment: string ) {
        const oldFiles = cloneDeep( this.model );
        file.comment = comment;
        this.filesChanged.emit( new FilesChangedEvent( oldFiles, cloneDeep( this.model ) ) );
    }

    protected fileUploadCss() {
        let styleClasses: string[] = [];
        styleClasses.push( 'file_upload' );

        if ( !isEmpty( this.configuration?.css ) ) {
            styleClasses.push( this.configuration?.css );
        } else {
            styleClasses.push( this.getStyleClassesIntern() );
        }

        if ( this.disabledIntern ) {
            styleClasses.push( 'file_upload_disabled' );
        }
        if ( this.configuration?.readonly ) {
            styleClasses.push( 'file_upload_readonly' );
        }

        return styleClasses.join( ' ' );
    }

    private getStyleClassesIntern(): string {
        if ( this.styleClass ) {
            return this.styleClass;
        } else if ( this.pFileUpload && this.pFileUpload.styleClass ) {
            return this.pFileUpload.styleClass;
        } else {
            return 'file_upload_standard';
        }
    }

    protected isDisableFileUpload(): boolean {
        if ( this.disabledIntern ) {
            return true;
        }
        if ( this.configuration?.readonly ) {
            return true;
        }

        return false;
    }

    protected showFilesSeparatorLine(): boolean {
        if ( this.effectiveCategoryIds()?.length === 1 && this.isShowCategoryIfUnique() ) {
            return true;
        }
        if ( this.effectiveCategoryIds()?.length > 1 && !this.isShowCategoryIfUnique() ) {
            return true;
        }
        return false;
    }

    protected getInternalUploadFieldLabel(): string {
        if ( !isEmpty( this.placeholder ) ) {
            return this.placeholder;
        }

        if ( this.isMultiple() ) {

            if ( this.configuration?.fileLimit == null ) {
                return this.hisUiService.translate( 'common.components.inputFile.chooseLabel.multipleUnbounded' );
            }

            return this.hisUiService.translate(
                'common.components.inputFile.chooseLabel.multipleBounded', this.configuration?.fileLimit );
        }

        return this.hisUiService.translate( 'common.components.inputFile.chooseLabel.single' );
    }

    protected invalidFileTypeMessage(): string {
        return this.hisUiService.translate( 'common.components.inputFile.invalidFileMessage', this.configuration?.accept?.replaceAll( ',', ', ' ) ?? '' ) + ' {0}';
    }

    private formatFileSize( bytes: number ): string {
        const formatter = new Intl.NumberFormat( 'en-US', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            minimumIntegerDigits: 1,
        } );
        if ( bytes === 0 ) {
            return '0 Bytes';
        }

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor( Math.log( bytes ) / Math.log( k ) );

        return formatter.format( ( bytes / Math.pow( k, i ) ) ) + ' ' + sizes[i];
    }

    protected invalidFileSizeMessage(): string {
        const maxSizeForUI = this.formatFileSize( this.configuration?.maxFileSize );
        return this.hisUiService.translate( 'common.components.inputFile.invalidSizeMessage', maxSizeForUI ) + '{0}';
    }

    protected getFeedback( file: HisUploadFile ): ResourceFeedback[] {
        if ( !this.feedback.has( file.uuid ) ) {
            return [];
        }

        return this.feedback.get( file.uuid );
    }

    protected showFeedback( event: Event, feedback: ResourceFeedback ): void {
        const translated = this.hisUiService.translate( feedback.feedbackMessageKey, ...feedback.feedbackMessageParams );

        this.feedbackPanelContent = translated;
        this.feedbackPanel.show( event );
    }

    protected uploadFiles( upload: FileUploadHandlerEvent | DragEvent ): void {
        if ( !this.largeFileSupport ) {
            this.addNewFilesFromStandardUpload( this.getFilesFromEvent( upload ) );
            return;
        }

        this.uploading = true;
        this.uploadQueue = this.getFilesFromEvent( upload ).map( file => this.buildTusUpload( file ) );
        this.uploadQueueChanged();
        this.startUploads();
    }

    private getFilesFromEvent( event: FileUploadHandlerEvent | DragEvent ): File[] {
        if ( event instanceof DragEvent ) {
            return Array.from( event.dataTransfer?.files ?? [] );
        }
        return event.files;
    }

    private buildTusUpload( file: File ): any {
        return {
            upload: new tus.Upload( file, {
                ...this.defaultUploadOptions,
                metadata: {
                    filename: file.name,
                    filetype: file.type
                },
                onProgress: ( bytesSent: number, bytesTotal: number ) => this.onProgess( file, bytesSent, bytesTotal ),
                onSuccess: () => this.onSuccess( file ),
                onError: ( error: Error | any ) => this.onError( file, error )
            } ),
            file: file,
            progress: 0,
            paused: false
        };
    }

    protected onProgess( file: File, bytesSent: number, bytesTotal: number ): void {
        this.hisBackendService.updateLastRefresh();
        const uploadElement = this.uploadQueue.find( ue => ue.file === file );
        if ( uploadElement ) {
            uploadElement.progress = ( Math.trunc( bytesSent / bytesTotal * 10000 ) ) / 100;
        }
    }

    protected onSuccess( file: File ): void {
        const uploadElementIndex = this.uploadQueue.findIndex( ue => ue.file === file );
        if ( uploadElementIndex !== -1 ) {
            const uploadElement = this.uploadQueue[uploadElementIndex];
            this.uploadQueue.splice( uploadElementIndex, 1 );
            this.uploadQueueChanged();
            this.addNewFiles( [this.uploadService.tusUploadToHisUploadFile( uploadElement.upload )] );
        }
        this.startUploads();
    }

    protected onError( file: File, error: Error | any ): void {
    }

    protected startUploads(): void {
        if ( this.uploadQueue.length === 0 ) {
            this.uploading = false;
            this.pFileUpload.clear();
            return;
        }
        this.uploading = true;
        const uploadElement = this.uploadQueue[0];
        const upload = uploadElement.upload;
        upload.findPreviousUploads().then( ( previousUploads: any[] ) => {
            if ( !previousUploads.length ) {
                upload.start();
                return;
            }
            const mostRecentUpload = previousUploads.sort( ( a, b ) => new Date( b.creationTime ).getTime() - new Date( a.creationTime ).getTime() )[0];
            const resume = confirm( this.hisUiService.translate( 'cs.sys.component.inputLargeFile.resumeUploadQuestion', upload.file.name, upload.file.size, mostRecentUpload.creationTime, mostRecentUpload.metadata.filename, mostRecentUpload.size ) );
            if ( resume ) {
                upload.resumeFromPreviousUpload( mostRecentUpload );
            }
            upload.start();
            uploadElement.paused = false;
            this.uploadQueueChanged();
        } );
    }

    /**
     * `true` while a file is actively being uploaded and the transfer is not
     * (fully) paused, see {@link uploadActive}.
     *
     * @deprecated prefer the {@link uploadActive} signal, which lets consumers react to a
     * change instead of having to poll during change detection.
     */
    get isUploadActive(): boolean {
        return this.uploadActive();
    }

    protected pauseUpload( uploadElement: { file: File; upload: any; progress: number; paused: boolean; } ): void {
        uploadElement.upload.abort();
        uploadElement.paused = true;
        this.uploadQueueChanged();
    }

    protected resumeUpload( uploadElement: { file: File; upload: any; progress: number; paused: boolean; } ): void {
        uploadElement.upload.start();
        uploadElement.paused = false;
        this.uploadQueueChanged();
    }

    protected cancelUpload( uploadElement: { file: File; upload: any; progress: number; paused: boolean; } ): void {
        uploadElement.upload.abort();
        const uploadElementIndex = this.uploadQueue.findIndex( ue => ue.file === uploadElement.file );
        if ( uploadElementIndex !== -1 ) {
            this.uploadQueue.splice( uploadElementIndex, 1 );
            this.uploadQueueChanged();
        }
        this.startUploads();
    }

    /** Notifies {@link uploadActive} that {@link uploadQueue} has been mutated. */
    protected uploadQueueChanged(): void {
        this.uploadQueueVersion.update( version => version + 1 );
    }

    ngOnDestroy() {
        this.uploadQueue.forEach( ue => ue.upload.abort() );
        this.uploadQueue = [];
        this.uploadQueueChanged();
    }

    protected feedbackPending( file: HisUploadFile ): boolean {
        return this.uploadService.isFeedbackRequestRunning( file.uuid );
    }

    private loadDocumentMetadataCategories(): void {
        const dmcIds = this.effectiveCategoryIds();
        if ( !dmcIds?.length ) {
            return;
        }

        this.documentMetadataCategoryService
            .getDocumentMetadataCategories()
            .subscribe( ( result: GenericValue[] ) => {
                if ( !result ) {
                    return;
                }

                const fromConfig = result.filter( c => dmcIds.includes( c.id ) );

                const files = this.model;
                const selected = files ? this.determineSelectedCategory( result, fromConfig, files.length > 0 ? files[0] : null ) : undefined;

                if ( selected ) {
                    this.selectedCategory.set( selected );
                    if ( !fromConfig.includes( selected ) ) {
                        fromConfig.push( selected );
                    }
                    this.setDocumentMetadataCategoryId( selected );
                }
                this.allAssignableConfigurationDmcValues.set( fromConfig );
            } );
    }

    private determineSelectedCategory( allCategories: GenericValue[], uploadConfigCategories: GenericValue[], firstFile: HisUploadFile ): GenericValue {
        const selected = this.selectedCategory();

        if ( uploadConfigCategories.length === 1 ) {
            return uploadConfigCategories[0];
        }

        if ( !firstFile ) {
            return selected;
        }

        const categoryFromUploadedFile = uploadConfigCategories.find( c => c.id === firstFile.documentMetadataCategoryId );
        if ( firstFile.documentMetadataCategoryId && !selected && categoryFromUploadedFile ) {
            return categoryFromUploadedFile;
        }

        const fileDmcId = firstFile.documentMetadataCategoryId;
        const missingCategory = allCategories.find( c => c.id === fileDmcId );
        if ( fileDmcId && !this.effectiveCategoryIds()?.includes( fileDmcId ) && missingCategory ) {
            return missingCategory;
        }

        return selected;
    }

    protected filterDocumentMetadataCategories( event: AutoCompleteCompleteEvent ): void {
        const query = event.query?.toLowerCase() ?? '';
        const all = this.allAssignableConfigurationDmcValues();

        const filtered = all.filter( dmc =>
            dmc?.defaulttext?.toLowerCase().includes( query )
        );

        this.filteredCategories.set( filtered );
    }

    protected selectDocumentMetadataCategory( event: AutoCompleteSelectEvent ): void {
        const category: GenericValue = event.value;
        this.selectedCategory.set( category );
        this.setDocumentMetadataCategoryId( category );
    }

    protected onCategoryModelChange( category: GenericValue ): void {
        this.selectedCategory.set( category );
        this.setDocumentMetadataCategoryId( category );
    }

    protected isShowDocumentMetadataCategories(): boolean {
        return this.showCategorySelect();
    }

    reset(): void {
        this.selectedCategory.set( null );
        this.control.reset();
    }
}
